import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import rbush from "geojson-rbush";
import { delay } from "lodash";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import { feature as toFeature } from "fond/turf";

import drawLineStringMode from "../../base/drawLineString";

import { isValid, pullInPoints } from "./helper";

/**
 * This is a custom mode for drawing ug paths in FOND Planner Extended.
 * It extends the draw_lineString mode and adds validation for ug points.
 */
const EMPTY_FC = { type: "FeatureCollection" as const, features: [] };

const updateSnapIndicator = (map: mapboxgl.Map, sourceId: string, snappedLng: number, snappedLat: number, e: MapboxDraw.MapMouseEvent) => {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  if (!source) return;

  const isSnapped = Math.abs(snappedLng - e.lngLat.lng) > 1e-8 || Math.abs(snappedLat - e.lngLat.lat) > 1e-8;

  if (isSnapped) {
    source.setData({
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "Point", coordinates: [snappedLng, snappedLat] }, properties: {} }],
    });
  } else {
    source.setData(EMPTY_FC);
  }
};

const drawLineString: DrawCustomMode = {
  ...drawLineStringMode,

  onMouseMove: function (this, state, e) {
    drawLineStringMode.onMouseMove?.call(this, state, e);

    const sourceId = state.options?.snapIndicatorSource;
    if (sourceId && state.snappedLng !== undefined && state.snappedLat !== undefined) {
      updateSnapIndicator(this.map, sourceId, state.snappedLng, state.snappedLat, e);
    }
  },

  onStop: function (this, state) {
    const sourceId = state.options?.snapIndicatorSource;
    if (sourceId) {
      const source = this.map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
      if (source) source.setData(EMPTY_FC);
    }

    drawLineStringMode.onStop?.call(this, state);

    // We allow the line to be created first, then we refresh all colocated
    // points valid property.
    delay(() => {
      const tree = rbush();
      tree.load(this._ctx.api.getAll());
      pullInPoints.call(this, toFeature(state.line)).forEach((f) => {
        if (f.id) this._ctx.api.setFeatureProperty(f.id, DRAWING_CONSTANTS.Valid, isValid(f, tree).toString());
        this.doRender(f.id);
      });
    }, 0);
  },
};

export default drawLineString;
